import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { ViewKey } from "../constants";
import { useAppContext } from "useAppContext";
import { useLoggedInAs } from "shared/hooks";
import { getSelectedAppView, setSelectedAppView } from "shared/utils";

const VALID_VIEW_KEYS = new Set<string>(Object.values(ViewKey));

export const useOrgHome = () => {
  const loggedInAs = useLoggedInAs();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initial view: prefer URL (for deep links), then session, then default
  const initialView = useMemo(() => {
    const fromUrl = searchParams.get("view");
    if (fromUrl && VALID_VIEW_KEYS.has(fromUrl)) return fromUrl as ViewKey;
    const lastView = getSelectedAppView("orgHome");
    return lastView && lastView.length > 0
      ? (lastView as ViewKey)
      : ViewKey.ActivityFeed;
  }, [searchParams]);

  const [selectedView, setSelectedView] = useState<ViewKey>(initialView);

  // Sync state when URL view param changes (e.g. browser back)
  useEffect(() => {
    const fromUrl = searchParams.get("view");
    if (fromUrl && VALID_VIEW_KEYS.has(fromUrl)) {
      setSelectedView(fromUrl as ViewKey);
    }
  }, [searchParams]);

  const {
    isInitialized,
    organizations,
    organizationsLoading,
    organizationsError,
  } = useAppContext();

  const organization = useMemo(
    () =>
      loggedInAs?.entityId
        ? organizations.find(org => org._id === loggedInAs.entityId) ?? null
        : null,
    [organizations, loggedInAs?.entityId],
  );

  const isOrganizationChecked =
    !!loggedInAs?.entityId && isInitialized && !organizationsLoading;

  const handleSelectedViewChange = (view: ViewKey) => {
    setSelectedView(view);
    setSelectedAppView("orgHome", view);
    const next = new URLSearchParams(searchParams);
    next.set("view", view);
    setSearchParams(next, { replace: true });
  };

  return {
    isInitialized,
    selectedView,
    onSelectedViewChange: handleSelectedViewChange,
    organization,
    isOrganizationChecked,
    loading: organizationsLoading,
    error: organizationsError,
  };
};
